/**
 * Look Panel Manager - Moon Moon Agency
 * Handles opening/closing of the look panel and variant selection within it.
 * Panels live outside the Splide DOM to avoid position:fixed + CSS transform conflicts.
 */

/*********************************************************************************************
 * 🔧 LOOK PANEL MANAGER
 */
const LookPanelManager = {
  config: {
    selectors: {
      close: '[data-look-panel-close]',
      variantInput: '[data-card-variant-input]',
      variantIdInput: '[data-look-variant-id]',
      addBtn: '[data-look-add-btn]',
      priceEl: '[data-look-panel-price]',
      productJson: '[data-look-product-json]',
    },
    classes: {
      open: 'mm-is-open',
      visible: 'mm-is-visible',
    },
  },

  _activeWrapper: null,
  _previousFocus: null,

  init() {
    this._bindEvents();
    window.log('info', '🎨 [LOOK_PANEL]', 'Initialized');
  },

  _bindEvents() {
    document.addEventListener('click', (e) => {
      // Open — trigger carries the panel ID as its attribute value
      const trigger = e.target.closest('[data-look-panel-trigger]');
      if (trigger) {
        e.preventDefault();
        const panelId = trigger.dataset.lookPanelTrigger;
        const wrapper = document.querySelector(`[data-look-panel-wrapper="${panelId}"]`);
        if (wrapper) this.open(wrapper, trigger);
        return;
      }

      // Close
      const closeEl = e.target.closest(this.config.selectors.close);
      if (closeEl) {
        const wrapper = closeEl.closest('[data-look-panel-wrapper]');
        if (wrapper) this.close(wrapper);
        return;
      }
    });

    // Variant change inside panel
    document.addEventListener('change', (e) => {
      const input = e.target.closest(this.config.selectors.variantInput);
      if (!input) return;
      const wrapper = input.closest('[data-look-panel-wrapper]');
      if (!wrapper) return;
      this._updateSelectedState(wrapper, input);
      this._onVariantChange(wrapper);
    });

    // Sync variant ID just before mm_cart.js reads FormData (capture phase fires first)
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-look-form]');
      if (!form) return;
      const wrapper = form.closest('[data-look-panel-wrapper]');
      if (!wrapper) return;
      this._syncVariantId(wrapper);
    }, true);

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._activeWrapper) {
        this.close(this._activeWrapper);
      }
    });

    // Close panel once the item has been added to the cart
    document.addEventListener('cart:add-to-cart-success', () => {
      if (this._activeWrapper) this.close(this._activeWrapper);
    });
  },

  open(wrapper, trigger) {
    this._previousFocus = trigger || document.activeElement;
    this._activeWrapper = wrapper;

    wrapper.classList.add(this.config.classes.visible);
    // Force reflow so the display change is committed before the
    // transform/opacity transition class is added on the next frame.
    wrapper.offsetHeight;

    requestAnimationFrame(() => {
      wrapper.classList.add(this.config.classes.open);
      this._refreshSplides(wrapper);
    });

    wrapper.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('stop-scrolling');

    const closeBtn = wrapper.querySelector(this.config.selectors.close);
    if (closeBtn) closeBtn.focus();

    window.log('info', '🎨 [LOOK_PANEL]', 'Opened');
  },

  close(wrapper) {
    wrapper.classList.remove(this.config.classes.open);
    wrapper.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('stop-scrolling');

    const panel = wrapper.querySelector('.mm-look-panel');
    const onTransitionEnd = (e) => {
      if (e.target !== panel) return;
      wrapper.classList.remove(this.config.classes.visible);
      panel.removeEventListener('transitionend', onTransitionEnd);
    };
    if (panel) panel.addEventListener('transitionend', onTransitionEnd);

    this._activeWrapper = null;

    if (this._previousFocus) {
      this._previousFocus.focus();
      this._previousFocus = null;
    }

    window.log('info', '🎨 [LOOK_PANEL]', 'Closed');
  },

  _refreshSplides(wrapper) {
    wrapper.querySelectorAll('.splide').forEach((el) => {
      if (el._splide) el._splide.refresh();
    });
  },

  _getProductData(wrapper) {
    const script = wrapper.querySelector(this.config.selectors.productJson);
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      window.log('error', '🎨 [LOOK_PANEL]', 'Failed to parse product JSON', { error: e.message });
      return null;
    }
  },

  _updateSelectedState(wrapper, changedInput) {
    wrapper.querySelectorAll(`input[name="${changedInput.name}"]`).forEach((input) => {
      const label = input.nextElementSibling;
      if (!label || label.tagName !== 'LABEL') return;
      label.classList.toggle('mm-selected', input.checked);
    });
  },

  _getSelectedOptions(wrapper) {
    const productData = this._getProductData(wrapper);
    const bundleOptions = productData?.options || null;
    const options = {};

    wrapper.querySelectorAll(this.config.selectors.variantInput).forEach(input => {
      if (!input.checked) return;

      let position = input.dataset.optionPosition;

      // Bundle inputs: bp_pos order may differ from bundle option order — match by value
      if (bundleOptions && input.hasAttribute('data-bundle-product')) {
        const match = bundleOptions.find(opt => opt.values.includes(input.value));
        if (match) position = match.position;
      }

      options[`option${position}`] = input.value;
    });

    return options;
  },

  _findVariant(productData, selectedOptions) {
    if (Object.keys(selectedOptions).length === 0) return null;
    return productData.variants.find(variant =>
      Object.keys(selectedOptions).every(key => variant[key] === selectedOptions[key])
    ) || null;
  },

  _syncVariantId(wrapper) {
    const productData = this._getProductData(wrapper);
    if (!productData) return;
    const selectedOptions = this._getSelectedOptions(wrapper);
    const variant = this._findVariant(productData, selectedOptions);
    if (!variant) return;
    const variantIdInput = wrapper.querySelector(this.config.selectors.variantIdInput);
    if (variantIdInput) variantIdInput.value = variant.id;
    window.log('info', '🎨 [LOOK_PANEL]', 'Variant synced on submit', { variantId: variant.id, selectedOptions });
  },

  _onVariantChange(wrapper) {
    const productData = this._getProductData(wrapper);
    if (!productData) return;

    const selectedOptions = this._getSelectedOptions(wrapper);
    const variant = this._findVariant(productData, selectedOptions);

    const variantIdInput = wrapper.querySelector(this.config.selectors.variantIdInput);
    const addBtn = wrapper.querySelector(this.config.selectors.addBtn);
    const priceEl = wrapper.querySelector(this.config.selectors.priceEl);

    if (variant) {
      if (variantIdInput) variantIdInput.value = variant.id;

      if (addBtn) {
        const newText = variant.available ? addBtn.dataset.addText : addBtn.dataset.soldOutText;
        addBtn.disabled = !variant.available;
        addBtn.querySelectorAll('[data-btn-label]').forEach(el => { el.textContent = newText; });
      }

      if (priceEl && variant.price !== undefined) {
        const formatted = this._formatMoney(variant.price);
        if (formatted) priceEl.textContent = formatted;
      }
    } else {
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.querySelectorAll('[data-btn-label]').forEach(el => { el.textContent = addBtn.dataset.soldOutText; });
      }
    }

    window.log('info', '🎨 [LOOK_PANEL]', 'Variant changed', { variant: variant?.id });
  },

  _formatMoney(cents) {
    if (typeof window.Shopify?.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    return (cents / 100).toLocaleString(document.documentElement.lang || 'fr', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  },
};

// Auto-init
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => LookPanelManager.init());
} else {
  LookPanelManager.init();
}

document.addEventListener('shopify:section:load', () => LookPanelManager.init());
